import { useEffect, useState, useCallback } from "react";
import { chListDocsInspeccion, chGenerarDocInspeccion, chBorrarDocumentoCh } from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import { toast } from "sonner";
import { FileText, FileDown, Loader2, Trash2, ClipboardList, ListChecks, LayoutList } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TIPOS = [
  { tipo: "rapport_inspection", label: "Informe de inspección", desc: "Resultados del checklist, no conformidades, peligros y fotos.", icon: ClipboardList },
  { tipo: "plan_mesures", label: "Plan de medidas correctoras", desc: "Tabla de medidas con prioridad, responsable, plazo y estado.", icon: ListChecks },
  { tipo: "fiches_postes", label: "Fichas por puesto", desc: "Una ficha por puesto con sus peligros y medidas.", icon: LayoutList },
];

export default function SuizaDocsInspeccion({ inspeccion }) {
  const iid = inspeccion.id;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState("");
  const [idioma, setIdioma] = useState("fr");

  const load = useCallback(() => {
    setLoading(true);
    chListDocsInspeccion(iid).then(setDocs).catch(() => {}).finally(() => setLoading(false));
  }, [iid]);
  useEffect(() => { load(); }, [load]);

  const generar = async (tipo) => {
    setGen(tipo);
    try { await chGenerarDocInspeccion(iid, { tipo, idioma }); toast.success("Documento generado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar"); }
    finally { setGen(""); }
  };
  const borrar = async (d) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try { await chBorrarDocumentoCh(d.id); toast.success("Documento eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div data-testid="ch-docs-inspeccion">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="font-heading font-semibold">Documentos de la inspección</h4>
        <select value={idioma} onChange={(e) => setIdioma(e.target.value)} data-testid="ch-docs-idioma" className="h-9 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500">
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </select>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        {TIPOS.map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.tipo} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col" data-testid={`ch-docgen-${t.tipo}`}>
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center mb-2"><Icon className="w-4 h-4" /></div>
              <div className="font-medium text-sm text-slate-900">{t.label}</div>
              <div className="text-xs text-slate-500 flex-1 mt-1">{t.desc}</div>
              <button onClick={() => generar(t.tipo)} disabled={!!gen} data-testid={`ch-docgen-btn-${t.tipo}`}
                className="mt-3 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
                {gen === t.tipo ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Generar
              </button>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : docs.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 text-center" data-testid="ch-docs-insp-vacio">
          Aún no has generado documentos para esta inspección.
        </div>
      ) : (
        <div className="space-y-2" data-testid="ch-docs-insp-lista">
          {docs.map((d) => (
            <div key={d.id} data-testid={`ch-doc-${d.id}`} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm text-slate-900 truncate flex items-center gap-2">
                  {d.tipo_label}
                  {d.version && <span data-testid={`ch-doc-version-${d.id}`} className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">v{d.version}</span>}
                </div>
                <div className="text-xs text-slate-400">{d.idioma === "fr" ? "Français" : "Español"} · {formatFecha(d.generado_at)}</div>
              </div>
              <a href={`${BACKEND}${d.pdf_url}`} target="_blank" rel="noreferrer" data-testid={`ch-doc-pdf-${d.id}`} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"><FileDown className="w-4 h-4" /> PDF</a>
              <a href={`${BACKEND}${d.docx_url}`} target="_blank" rel="noreferrer" data-testid={`ch-doc-docx-${d.id}`} className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50">Word</a>
              <button onClick={() => borrar(d)} data-testid={`ch-doc-del-${d.id}`} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
